import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import ResendVerification from '../components/ResendVerification';
import { authAPI } from '../services/api';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyAndRedirect = async () => {
      const token = searchParams.get('token');
      const tier = searchParams.get('tier');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        // Step 1: Verify email — backend returns access_token for auto-login
        const response = await authAPI.verifyEmail(token);
        
        const accessToken = response.data.access_token;
        
        if (accessToken) {
          localStorage.setItem('access_token', accessToken);
        }

        // Step 2: Redirect based on tier
        if (tier && accessToken) {
          // All tiers require payment setup (beginner/casual start with 14-day trial)
          setStatus('success');
          setMessage('Email verified! Redirecting to payment...');
          setTimeout(() => navigate(`/payment?tier=${tier}`), 1500);
        } else if (accessToken) {
          // No tier param — go to dashboard
          setStatus('success');
          setMessage('Email verified successfully! Redirecting to dashboard...');
          setTimeout(() => navigate('/dashboard'), 2000);
        } else {
          setStatus('success');
          setMessage('Email verified successfully! Please log in.');
          setTimeout(() => navigate('/login'), 2000);
        }
        
      } catch (err: any) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. Please try again.');
      }
    };

    verifyAndRedirect();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* SEO — React 19 hoists to <head> */}
      <title>Verify Email — NWC-Analytics</title>
      <meta name="description" content="Verify your email address to activate your NWC-Analytics account." />
      <link rel="canonical" href="https://nwc-analytics.com/verify-email" />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <img src="/images/logo.png" alt="NWC-Analytics" className="h-50 w-50" />
          <span className="text-xl font-bold text-primary-400 dark:text-primary-400" style={{ fontFamily: "'Viner Hand ITC', 'Caveat', cursive", fontSize: '1rem', fontStyle: 'italic' }}>NWC-Analytics</span>
        </Link>
        
        <div className="mt-8 bg-white dark:bg-gray-700 py-8 px-4 shadow-xl dark:shadow-gray-200/20 sm:rounded-lg sm:px-10 border dark:border-gray-500">
          <div className="text-center">
            {status === 'verifying' && (
              <>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/50">
                  <svg className="animate-spin h-6 w-6 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Verifying your email...</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Please wait</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/50">
                  <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Email Verified!</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{message}</p>
                <div className="mt-4">
                  <svg className="animate-spin h-5 w-5 text-primary-500 mx-auto" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </>
            )}
            
            {status === 'error' && (
              <>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/50">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Verification Failed</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{message}</p>

                {/* The backend's expired-token message says "request a new one" —
                    this is what makes that actually possible. The token is invalid
                    so we no longer know who they are; ask for the address. */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600 text-left">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Need a new verification link? Enter your email and we'll send a fresh one.
                  </p>
                  <ResendVerification selectedTier={searchParams.get('tier') || undefined} />
                </div>

                <div className="mt-6 space-y-3">
                  <Link to="/login" className="block text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;